import { useEffect, useState } from "react";
import { getTeamSettings, updateTeamSettings } from "services/team";
import Toast from "components/common/Toast";

const getTeamId = () => {
  try {
    return JSON.parse(localStorage.getItem("selectedTeam") || "{}")
      ?.currentTeamId;
  } catch {
    return undefined;
  }
};

type CommPrefs = {
  emailEnabled: boolean;
  contentChange: boolean;
  similarProducts: boolean;
  officeActions: boolean;
};

const DEFAULT_PREFS: CommPrefs = {
  emailEnabled: false,
  contentChange: false,
  similarProducts: false,
  officeActions: false,
};

const CommunicationsSettings = () => {
  const [prefs, setPrefs] = useState<CommPrefs>(DEFAULT_PREFS);
  // Last-saved value, used to detect changes (dirty state) and reset on Cancel.
  const [savedPrefs, setSavedPrefs] = useState<CommPrefs>(DEFAULT_PREFS);
  const [isSaving, setIsSaving] = useState(false);

  // Load the team's current communication preferences.
  useEffect(() => {
    const teamId = getTeamId();
    if (!teamId) return;
    (async () => {
      try {
        const res: any = await getTeamSettings(teamId);
        const settings = res?.data ?? res;
        const comm = settings?.communication_settings;
        if (comm) {
          const next: CommPrefs = {
            emailEnabled: !!comm.email_enabled,
            contentChange: !!comm.content_change,
            similarProducts: !!comm.similar_products,
            officeActions: !!comm.office_actions,
          };
          setPrefs(next);
          setSavedPrefs(next);
        }
      } catch (error) {
        console.error("Error fetching communication settings:", error);
      }
    })();
  }, []);

  const handleSave = async () => {
    const teamId = getTeamId();
    if (!teamId) return;
    setIsSaving(true);
    try {
      await updateTeamSettings({
        team_id: teamId,
        communication_settings: {
          email_enabled: prefs.emailEnabled,
          // Sub-preferences only matter when email is enabled.
          content_change: prefs.emailEnabled && prefs.contentChange,
          similar_products: prefs.emailEnabled && prefs.similarProducts,
          office_actions: prefs.emailEnabled && prefs.officeActions,
        },
      });
      setSavedPrefs(prefs);
      Toast.success("Success", "Communication preferences saved");
    } catch (error) {
      console.error("Error saving communication settings:", error);
      Toast.error("Error", "Failed to save communication preferences");
    } finally {
      setIsSaving(false);
    }
  };

  // Reset back to the last-saved value.
  const handleCancel = () => {
    setPrefs(savedPrefs);
  };

  const isDirty =
    prefs.emailEnabled !== savedPrefs.emailEnabled ||
    prefs.contentChange !== savedPrefs.contentChange ||
    prefs.similarProducts !== savedPrefs.similarProducts ||
    prefs.officeActions !== savedPrefs.officeActions;
  const saveDisabled = !isDirty || isSaving;

  return (
    <div className="mt-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h4 className="text-lg font-interSemiBold text-[#101828] pb-0.5">
            Communications
          </h4>
          <p className="text-sm text-[#475467]">
            Choose which email updates you'd like to receive from IpVijil.
          </p>
        </div>
        <div className="flex items-center gap-x-3">
          <button
            type="button"
            onClick={handleCancel}
            className="cancle-btn-blue !px-[15px] !text-sm hover:bg-[#F2F4F7]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saveDisabled}
            className={`submit-btn-blue !px-[15px] !text-sm hover:bg-[#120693] ${
              saveDisabled ? "button-disabled" : ""
            }`}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="w-full h-px bg-[#EAECF0] mb-6"></div>

      <div className="max-w-[620px] border border-[#EAECF0] rounded-lg p-6">
        {/* Master email toggle */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={prefs.emailEnabled}
            onChange={(e) =>
              setPrefs((p) => ({ ...p, emailEnabled: e.target.checked }))
            }
            className="mt-0.5 w-4 h-4 text-[#1751D0] border border-gray-300 rounded focus:ring-[#1751D0] focus:ring-2"
          />
          <span>
            <span className="block text-sm font-interSemiBold text-[#101828]">
              Receive email notifications
            </span>
            <span className="block text-sm text-[#475467]">
              Get notified by email when there are important updates on the
              patents you monitor.
            </span>
          </span>
        </label>

        {/* Sub-preferences — grouped by category, active only when email is on */}
        <div
          className={`mt-5 flex flex-col gap-4 ${
            prefs.emailEnabled
              ? ""
              : "opacity-50 pointer-events-none select-none"
          }`}
        >
          {/* New Prosecution */}
          <div className="border border-[#EAECF0] rounded-lg p-4">
            <h5 className="text-sm font-interSemiBold text-[#101828] mb-3">
              New Prosecution
            </h5>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={prefs.officeActions}
                disabled={!prefs.emailEnabled}
                onChange={(e) =>
                  setPrefs((p) => ({ ...p, officeActions: e.target.checked }))
                }
                className="mt-0.5 w-4 h-4 text-[#1751D0] border border-gray-300 rounded focus:ring-[#1751D0] focus:ring-2"
              />
              <span>
                <span className="block text-sm font-interMedium text-[#101828]">
                  New office action found
                </span>
                <span className="block text-sm text-[#475467]">
                  Email me when a new office action is found for a patent I'm
                  monitoring.
                </span>
              </span>
            </label>
          </div>

          {/* New Product */}
          <div className="border border-[#EAECF0] rounded-lg p-4">
            <h5 className="text-sm font-interSemiBold text-[#101828] mb-3">
              New Product
            </h5>
            <div className="flex flex-col gap-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={prefs.contentChange}
                  disabled={!prefs.emailEnabled}
                  onChange={(e) =>
                    setPrefs((p) => ({ ...p, contentChange: e.target.checked }))
                  }
                  className="mt-0.5 w-4 h-4 text-[#1751D0] border border-gray-300 rounded focus:ring-[#1751D0] focus:ring-2"
                />
                <span>
                  <span className="block text-sm font-interMedium text-[#101828]">
                    Website content changed
                  </span>
                  <span className="block text-sm text-[#475467]">
                    Email me when the content of a monitored website changes for
                    a patent I'm monitoring.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={prefs.similarProducts}
                  disabled={!prefs.emailEnabled}
                  onChange={(e) =>
                    setPrefs((p) => ({ ...p, similarProducts: e.target.checked }))
                  }
                  className="mt-0.5 w-4 h-4 text-[#1751D0] border border-gray-300 rounded focus:ring-[#1751D0] focus:ring-2"
                />
                <span>
                  <span className="block text-sm font-interMedium text-[#101828]">
                    New similar product found
                  </span>
                  <span className="block text-sm text-[#475467]">
                    Email me when a new similar product is found for a patent I'm
                    monitoring.
                  </span>
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunicationsSettings;
